import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { RouteConfig } from '@app/routing/RenderRoutes'

import { Navigate, useNavigate } from '@lib/react-router-dom'

import { useSessionEntity } from '@context/EntityContext'
import { CorporateFormValues, HasEmirStep, useSignup } from '@context/SignupContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { isTickmillEUType, isTickmillUKType } from '@utils/companyName.utils'

import { CompanyDetailsStep1FormValues } from '../Pages/CompanyDetails/CompanyDetailsStep1/CompanyDetailsStep1Form'
import { CompanyDetailsStep1Page } from '../Pages/CompanyDetails/CompanyDetailsStep1/CompanyDetailsStep1Page'
import { CompanyDetailsStep2FormValues } from '../Pages/CompanyDetails/CompanyDetailsStep2/CompanyDetailsStep2Form'
import { CompanyDetailsStep2Page } from '../Pages/CompanyDetails/CompanyDetailsStep2/CompanyDetailsStep2Page'
import { CompanyDetailsStep3FormValues } from '../Pages/CompanyDetails/CompanyDetailsStep3/CompanyDetailsStep3Form'
import { CompanyDetailsStep3Page } from '../Pages/CompanyDetails/CompanyDetailsStep3/CompanyDetailsStep3Page'
import {
  CompanyDetailsStep4FormValues,
  CompanyDetailsStep4Page,
} from '../Pages/CompanyDetails/CompanyDetailsStep4/CompanyDetailsStep4Page'
import { useCreateClient } from '../hooks/useCreateClient'

export type CompanyStepPath = 'basic' | 'legal' | 'regulatory' | 'emir-test'

export const useCompanyDetailsRoutes = (): RouteConfig[] => {
  const navigate = useNavigate()
  const entity = useSessionEntity()
  const apiClient = useApiClient(ClientApiClient)
  const { signupData, updateCorpValues, updateTotalSteps } = useSignup()
  const { denormalize, signInAndMoveToFinancialInfo } = useCreateClient()
  const [submitLoading, setSubmitLoading] = useState(false)

  const hasEmirStep = useMemo(
    () =>
      (isTickmillEUType(
        entity ?? signupData.lead?.tickmillCompany.id ?? signupData.values.tickmillCompanyId
      ) ||
        isTickmillUKType(
          entity ?? signupData.lead?.tickmillCompany.id ?? signupData.values.tickmillCompanyId
        )) ??
      false,
    [entity, signupData.lead?.tickmillCompany.id, signupData.values.tickmillCompanyId]
  )

  useEffect(() => {
    updateTotalSteps({ companyDetails: hasEmirStep ? HasEmirStep.Yes : HasEmirStep.No })
  }, [hasEmirStep, updateTotalSteps])

  const stepPaths = useMemo<readonly CompanyStepPath[]>(() => {
    const paths: CompanyStepPath[] = ['basic', 'legal', 'regulatory']
    if (hasEmirStep) {
      paths.push('emir-test')
    }
    return paths
  }, [hasEmirStep])

  const nextPath = useCallback(
    (current: CompanyStepPath | undefined) => {
      const idx = current ? stepPaths.indexOf(current) : -1
      return stepPaths[idx + 1] ?? stepPaths[0]
    },
    [stepPaths]
  )

  const location = useLocation()

  type StepValues =
    | Partial<CompanyDetailsStep1FormValues>
    | Partial<CompanyDetailsStep2FormValues>
    | Partial<CompanyDetailsStep3FormValues>
    | Partial<CompanyDetailsStep4FormValues>

  const handleSubmit = useCallback(
    async (values: Partial<CompanyDetailsStep3FormValues | CompanyDetailsStep4FormValues>) => {
      try {
        setSubmitLoading(true)
        const corporateData = {
          ...signupData.corporateValues,
          ...values,
        } as CorporateFormValues
        updateCorpValues(corporateData)
        if (!signupData.lead) {
          return
        }
        await apiClient.createClient(denormalize(signupData.lead, signupData.values, corporateData))
        await signInAndMoveToFinancialInfo(signupData.values)
      } catch (e: unknown) {
        console.error(e)
      } finally {
        setSubmitLoading(false)
      }
    },
    [
      apiClient,
      denormalize,
      signInAndMoveToFinancialInfo,
      signupData.corporateValues,
      signupData.lead,
      signupData.values,
      updateCorpValues,
    ]
  )

  const nextStep = useCallback(
    async (values?: StepValues) => {
      if (values) {
        updateCorpValues(values as any)
      }
      const current = location.pathname.split('/').filter(Boolean).pop() as
        | CompanyStepPath
        | undefined
      if (current === 'regulatory' && !hasEmirStep) {
        return handleSubmit(values as Partial<CompanyDetailsStep3FormValues>)
      }
      const next = nextPath(current)
      navigate(`/sign-up/company-details/${next}`)
    },
    [updateCorpValues, location.pathname, hasEmirStep, handleSubmit, nextPath, navigate]
  )

  const subSteps: RouteConfig<CompanyStepPath>[] = useMemo(
    () => [
      {
        path: 'basic',
        element: <CompanyDetailsStep1Page onSubmit={nextStep} />,
      },
      {
        path: 'legal',
        element: <CompanyDetailsStep2Page onSubmit={nextStep} />,
      },
      {
        path: 'regulatory',
        element: <CompanyDetailsStep3Page submitLoading={submitLoading} onSubmit={nextStep} />,
      },
      ...(!hasEmirStep
        ? []
        : ([
            {
              path: 'emir-test',
              element: (
                <CompanyDetailsStep4Page submitLoading={submitLoading} onSubmit={handleSubmit} />
              ),
            },
          ] as RouteConfig<CompanyStepPath>[])),
    ],
    [submitLoading, hasEmirStep, handleSubmit, nextStep]
  )

  return useMemo(
    (): RouteConfig[] => [
      {
        path: 'company-details',
        element: <Navigate to={`/sign-up/company-details/${subSteps[0].path}`} />,
        children: [
          ...subSteps,
          { path: '*', element: <Navigate to={`/sign-up/company-details/${subSteps[0].path}`} /> },
        ],
      },
    ],
    [subSteps]
  )
}
